import { useEffect, useRef } from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import TextField from '@mui/material/TextField';
import { composeRepositoryName } from './ComposeRepositoryName';
import { META_TEMPLATE, repositoryNameError } from './RepositoryNameError';

/**
 * The repository name box, filled in from the System, Service Group and Service
 * name fields above it.
 *
 * Those three are separate parameters on the same step, read LIVE from
 * `formContext.formData` - the same mechanism SortedEntityField's
 * `filterBySibling` uses. Whenever one of them changes, the name is rebuilt as
 * `[system]-[service-group]-[service-name]`.
 *
 * It stays an ordinary text box. A user who types a name without touching the
 * dropdowns is left alone, and a typed edit is kept until one of the three
 * inputs changes again, at which point the composed name replaces it. A name
 * already present when the form opens is never overwritten on mount.
 *
 * The naming rules are shown here as the user types, in the same words the
 * worker uses. Which pattern applies depends on the Template Repository field,
 * read the same way as the three name parts.
 */
export const RepositoryNameField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, formData, schema, required, rawErrors, formContext } =
    props;

  const siblings = (
    formContext as { formData?: Record<string, unknown> } | undefined
  )?.formData;
  const composed = composeRepositoryName({
    systemName: siblings?.systemName as string | undefined,
    serviceGroupName: siblings?.serviceGroupName as string | undefined,
    serviceName: siblings?.serviceName as string | undefined,
  });

  const nameError = repositoryNameError(
    formData,
    siblings?.templateRepo === META_TEMPLATE,
  );

  // rjsf hands down a new onChange on every render, so it lives in a ref rather
  // than being an effect dependency - see GitHubUserField for the same reason.
  const onChangeRef = useRef(onChange);
  const lastComposed = useRef(composed);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (composed !== lastComposed.current) {
      lastComposed.current = composed;
      onChangeRef.current(composed || undefined);
    }
  }, [composed]);

  return (
    <TextField
      fullWidth
      label={(schema.title as string) ?? 'Repository Name'}
      helperText={nameError ?? (schema.description as string | undefined)}
      required={required}
      error={Boolean(nameError) || Boolean(rawErrors?.length)}
      value={formData ?? ''}
      onChange={event => onChange(event.target.value || undefined)}
    />
  );
};
